/**
 * Product Edit Page
 *
 * Full product editor with images, categories, variants, pricing, inventory, etc.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { api } from '../../lib/api';
import { ProductImageUpload, CategorySelector, VariantsEditor } from '../../components/ecommerce';
import type { ProductOption, ProductVariant } from '../../components/ecommerce';

interface Store {
  id: string;
  name: string;
  slug: string;
  currency: string;
}

interface OutletContext {
  store: Store;
}

interface Product {
  id: string;
  name: string;
  slug: string;
  description?: string;
  short_description?: string;
  product_type: 'physical' | 'digital' | 'service' | 'affiliate';
  status: 'draft' | 'active' | 'archived';
  visible: boolean;
  price: number;
  compare_at_price?: number;
  currency: string;
  track_inventory: boolean;
  inventory_quantity: number;
  images: string[];
  featured_image_index: number;
  categories: string[];
  options: ProductOption[];
  variants: ProductVariant[];
  weight?: number;
  requires_shipping: boolean;
  seo_title?: string;
  seo_description?: string;
  // Affiliate-specific fields
  affiliate_url?: string;
  affiliate_source?: string;
  affiliate_product_id?: string;
  affiliate_commission_rate?: number;
  affiliate_click_count?: number;
}

const DEFAULT_PRODUCT: Partial<Product> = {
  name: '',
  slug: '',
  description: '',
  short_description: '',
  product_type: 'physical',
  status: 'draft',
  visible: true,
  price: 0,
  compare_at_price: undefined,
  currency: 'USD',
  track_inventory: false,
  inventory_quantity: 0,
  images: [],
  featured_image_index: 0,
  categories: [],
  options: [],
  variants: [],
  weight: undefined,
  requires_shipping: true,
  seo_title: '',
  seo_description: '',
};

type TabId = 'general' | 'affiliate' | 'images' | 'categories' | 'variants' | 'inventory' | 'seo';

export default function ProductEditPage() {
  const { storeId, productId } = useParams<{ storeId: string; productId: string }>();
  const navigate = useNavigate();
  const context = useOutletContext<OutletContext | null>();
  const store = context?.store;

  const [product, setProduct] = useState<Partial<Product>>(DEFAULT_PRODUCT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('general');

  const isNew = productId === 'new';
  const effectiveStoreId = store?.id || storeId || '';

  const loadProduct = useCallback(async () => {
    if (isNew) {
      setProduct({ ...DEFAULT_PRODUCT, currency: store?.currency || 'USD' });
      setLoading(false);
      return;
    }

    const sid = store?.id || storeId;
    if (!sid || !productId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/api/ecommerce/stores/${sid}/products/${productId}`);
      const data = await response.json() as { product: Product };
      if (data.product) {
        // Parse options and variants from stored JSON
        const loadedProduct = {
          ...data.product,
          options: Array.isArray(data.product.options) ? data.product.options : [],
          variants: Array.isArray(data.product.variants) ? data.product.variants : [],
          featured_image_index: data.product.featured_image_index || 0,
          visible: data.product.visible !== false, // Default to true for existing products
        };
        setProduct(loadedProduct);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load product');
    } finally {
      setLoading(false);
    }
  }, [store?.id, storeId, productId, isNew, store?.currency]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const handleSave = async () => {
    const sid = store?.id || storeId;
    if (!sid || !product.name?.trim()) {
      setError('Product name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        name: product.name,
        slug: product.slug,
        description: product.description,
        short_description: product.short_description,
        product_type: product.product_type,
        status: product.status,
        visible: product.visible,
        price: product.price,
        compare_at_price: product.compare_at_price || null,
        track_inventory: product.track_inventory,
        inventory_quantity: product.inventory_quantity,
        images: product.images,
        featured_image_index: product.featured_image_index,
        categories: product.categories,
        options: product.options,
        variants: product.variants,
        weight: product.weight || null,
        requires_shipping: product.requires_shipping,
        seo_title: product.seo_title || null,
        seo_description: product.seo_description || null,
        // Affiliate fields
        affiliate_url: product.affiliate_url || null,
        affiliate_source: product.affiliate_source || null,
        affiliate_product_id: product.affiliate_product_id || null,
        affiliate_commission_rate: product.affiliate_commission_rate || null,
      };

      if (isNew) {
        const response = await api.post(`/api/ecommerce/stores/${sid}/products`, payload);
        const data = await response.json() as { product: Product };
        if (data.product) {
          navigate(`/store/${sid}/products/${data.product.id}`, { replace: true });
        }
      } else {
        await api.put(`/api/ecommerce/stores/${sid}/products/${productId}`, payload);
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const sid = store?.id || storeId;
    if (!sid || !productId || isNew) return;

    try {
      setDeleting(true);
      setError(null);
      await api.delete(`/api/ecommerce/stores/${sid}/products/${productId}`);
      navigate(`/store/${sid}/products`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete product');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const formatPriceForInput = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  const parsePriceFromInput = (value: string) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : Math.round(num * 100);
  };

  // Dynamic tabs based on product type
  const isAffiliate = product.product_type === 'affiliate';

  const tabs: { id: TabId; label: string; icon: React.ReactNode; hidden?: boolean }[] = [
    {
      id: 'general',
      label: 'General',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      id: 'affiliate',
      label: 'Affiliate',
      hidden: !isAffiliate,
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
    },
    {
      id: 'images',
      label: 'Images',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      id: 'categories',
      label: 'Categories',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
      ),
    },
    {
      id: 'variants',
      label: 'Variants',
      hidden: isAffiliate, // Hide for affiliate products
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
    },
    {
      id: 'inventory',
      label: 'Inventory',
      hidden: isAffiliate, // Hide for affiliate products
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
    },
    {
      id: 'seo',
      label: 'SEO',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/store/${store?.id || storeId}/products`)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? 'New Product' : product.name || 'Edit Product'}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              {isNew ? 'Create a new product' : 'Edit product details'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isNew && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              Delete
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </>
            ) : (
              'Save Product'
            )}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          Product saved successfully!
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto">
        <nav className="flex gap-1 min-w-max">
          {tabs.filter(tab => !tab.hidden).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors relative whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-teal-600 dark:text-teal-400'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600 dark:bg-teal-400" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* General Tab */}
        {activeTab === 'general' && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Basic Information
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={product.name || ''}
                    onChange={(e) => setProduct({ ...product, name: e.target.value })}
                    placeholder="e.g., Premium Cotton T-Shirt"
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    URL Slug
                  </label>
                  <div className="flex items-center">
                    <span className="text-gray-500 dark:text-gray-400 text-sm mr-2">/s/{store?.slug}/p/</span>
                    <input
                      type="text"
                      value={product.slug || ''}
                      onChange={(e) => setProduct({ ...product, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                      placeholder="premium-cotton-t-shirt"
                      className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Short Description
                  </label>
                  <input
                    type="text"
                    value={product.short_description || ''}
                    onChange={(e) => setProduct({ ...product, short_description: e.target.value })}
                    placeholder="Brief product summary (shown in listings)"
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Description
                  </label>
                  <textarea
                    value={product.description || ''}
                    onChange={(e) => setProduct({ ...product, description: e.target.value })}
                    placeholder="Detailed product description..."
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Status & Visibility
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={product.status || 'draft'}
                    onChange={(e) => setProduct({ ...product, status: e.target.value as Product['status'] })}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Product Type
                  </label>
                  <select
                    value={product.product_type || 'physical'}
                    onChange={(e) => {
                      const newType = e.target.value as Product['product_type'];
                      setProduct({
                        ...product,
                        product_type: newType,
                        // Reset affiliate fields when switching away from affiliate
                        ...(newType !== 'affiliate' && {
                          requires_shipping: newType === 'physical',
                        }),
                        // Auto-switch to affiliate tab when selecting affiliate type
                      });
                      if (newType === 'affiliate') {
                        setActiveTab('affiliate');
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="physical">Physical Product</option>
                    <option value="digital">Digital Product</option>
                    <option value="service">Service</option>
                    <option value="affiliate">Affiliate Product</option>
                  </select>
                  {product.product_type === 'affiliate' && (
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      Customers will be redirected to external affiliate link
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">Visible on Storefront</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Product must be Active AND Visible to appear on your store
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setProduct({ ...product, visible: !product.visible })}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    product.visible ? 'bg-teal-600' : 'bg-gray-200 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                      product.visible ? 'translate-x-7' : ''
                    }`}
                  />
                </button>
              </div>

              {product.status === 'active' && product.visible && (
                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    This product will be visible to customers on your storefront
                  </p>
                </div>
              )}

              {product.status === 'active' && !product.visible && (
                <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Product is active but hidden. Enable visibility to show on storefront.
                  </p>
                </div>
              )}

              {product.status !== 'active' && (
                <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Set status to "Active" to publish this product
                  </p>
                </div>
              )}
            </div>

            {/* Base Pricing */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Base Pricing
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Price ({product.currency || 'USD'})
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={formatPriceForInput(product.price || 0)}
                      onChange={(e) => setProduct({ ...product, price: parsePriceFromInput(e.target.value) })}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="w-full pl-8 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Compare at Price
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={product.compare_at_price ? formatPriceForInput(product.compare_at_price) : ''}
                      onChange={(e) => setProduct({ ...product, compare_at_price: e.target.value ? parsePriceFromInput(e.target.value) : undefined })}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="w-full pl-8 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Original price (shown as strikethrough)
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Affiliate Tab */}
        {activeTab === 'affiliate' && isAffiliate && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Affiliate Link Settings
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Affiliate URL *
                  </label>
                  <input
                    type="url"
                    value={product.affiliate_url || ''}
                    onChange={(e) => setProduct({ ...product, affiliate_url: e.target.value })}
                    placeholder="https://s.click.aliexpress.com/e/..."
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    The affiliate link customers will be redirected to when they click "Buy Now"
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Affiliate Source
                    </label>
                    <select
                      value={product.affiliate_source || ''}
                      onChange={(e) => setProduct({ ...product, affiliate_source: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="">Select source...</option>
                      <option value="aliexpress">AliExpress</option>
                      <option value="amazon">Amazon Associates</option>
                      <option value="ebay">eBay Partner Network</option>
                      <option value="shopee">Shopee Affiliate</option>
                      <option value="cj">CJ Affiliate</option>
                      <option value="shareasale">ShareASale</option>
                      <option value="rakuten">Rakuten</option>
                      <option value="awin">Awin</option>
                      <option value="impact">Impact</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Original Product ID
                    </label>
                    <input
                      type="text"
                      value={product.affiliate_product_id || ''}
                      onChange={(e) => setProduct({ ...product, affiliate_product_id: e.target.value })}
                      placeholder="e.g., 1005006123456789"
                      className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Commission Rate (%)
                  </label>
                  <div className="relative w-48">
                    <input
                      type="number"
                      value={product.affiliate_commission_rate || ''}
                      onChange={(e) => setProduct({ ...product, affiliate_commission_rate: parseFloat(e.target.value) || undefined })}
                      step="0.1"
                      min="0"
                      max="100"
                      placeholder="e.g., 5.0"
                      className="w-full px-4 py-2 pr-8 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    For tracking your potential earnings
                  </p>
                </div>
              </div>
            </div>

            {/* Affiliate Analytics */}
            {!isNew && product.affiliate_click_count !== undefined && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Affiliate Analytics
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {product.affiliate_click_count || 0}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Total Clicks</div>
                  </div>
                  {product.affiliate_commission_rate && (
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        ${(((product.price || 0) / 100) * (product.affiliate_commission_rate / 100) * (product.affiliate_click_count || 0) * 0.02).toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Est. Earnings (2% conv.)</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* How Affiliate Products Work */}
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800 p-6">
              <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                How Affiliate Products Work
              </h3>
              <ul className="space-y-2 text-sm text-purple-800 dark:text-purple-200">
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 dark:text-purple-400 mt-0.5">1.</span>
                  <span>Customers browse your store and see the affiliate product like any other product</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 dark:text-purple-400 mt-0.5">2.</span>
                  <span>Instead of "Add to Cart", they see a "Buy Now" button that redirects to the affiliate link</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 dark:text-purple-400 mt-0.5">3.</span>
                  <span>When they purchase through your link, you earn the affiliate commission</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-600 dark:text-purple-400 mt-0.5">4.</span>
                  <span>All clicks are tracked so you can monitor performance</span>
                </li>
              </ul>
            </div>
          </>
        )}

        {/* Images Tab */}
        {activeTab === 'images' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Product Images
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Upload product images. The featured image will be shown in product listings.
            </p>
            <ProductImageUpload
              storeId={effectiveStoreId}
              productId={productId !== 'new' ? productId : undefined}
              images={product.images || []}
              featuredIndex={product.featured_image_index || 0}
              onChange={(images, featuredIndex) => {
                setProduct({ ...product, images, featured_image_index: featuredIndex });
              }}
            />
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Product Categories
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Organize your product into categories. You can create nested categories for better organization.
            </p>
            <CategorySelector
              storeId={effectiveStoreId}
              selectedCategories={product.categories || []}
              onChange={(categories) => setProduct({ ...product, categories })}
            />
          </div>
        )}

        {/* Variants Tab */}
        {activeTab === 'variants' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Product Variants
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Create options like Size or Color, then set individual prices and inventory for each variant combination.
            </p>
            <VariantsEditor
              options={product.options || []}
              variants={product.variants || []}
              basePrice={product.price || 0}
              currency={product.currency || 'USD'}
              onChange={(options, variants) => {
                setProduct({ ...product, options, variants });
              }}
            />
          </div>
        )}

        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Inventory & Shipping
            </h2>
            <div className="space-y-4">
              {(product.variants?.length || 0) > 0 && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    SKUs are managed per variant. Go to the Variants tab to set SKUs for each variant.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">Track Inventory</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Enable to track stock levels</p>
                </div>
                <button
                  type="button"
                  onClick={() => setProduct({ ...product, track_inventory: !product.track_inventory })}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    product.track_inventory ? 'bg-teal-600' : 'bg-gray-200 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                      product.track_inventory ? 'translate-x-7' : ''
                    }`}
                  />
                </button>
              </div>

              {product.track_inventory && (product.variants?.length || 0) === 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Quantity in Stock
                  </label>
                  <input
                    type="number"
                    value={product.inventory_quantity || 0}
                    onChange={(e) => setProduct({ ...product, inventory_quantity: parseInt(e.target.value) || 0 })}
                    min="0"
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              )}

              {product.track_inventory && (product.variants?.length || 0) > 0 && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    Inventory is tracked per variant. Go to the Variants tab to set stock quantities.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">Requires Shipping</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Physical product that needs to be shipped</p>
                </div>
                <button
                  type="button"
                  onClick={() => setProduct({ ...product, requires_shipping: !product.requires_shipping })}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    product.requires_shipping ? 'bg-teal-600' : 'bg-gray-200 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                      product.requires_shipping ? 'translate-x-7' : ''
                    }`}
                  />
                </button>
              </div>

              {product.requires_shipping && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Weight (grams)
                  </label>
                  <input
                    type="number"
                    value={product.weight || ''}
                    onChange={(e) => setProduct({ ...product, weight: e.target.value ? parseInt(e.target.value) : undefined })}
                    min="0"
                    placeholder="e.g., 500"
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* SEO Tab */}
        {activeTab === 'seo' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Search Engine Optimization
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SEO Title
                </label>
                <input
                  type="text"
                  value={product.seo_title || ''}
                  onChange={(e) => setProduct({ ...product, seo_title: e.target.value })}
                  placeholder={product.name || 'Product title for search engines'}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {(product.seo_title || product.name || '').length}/60 characters
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SEO Description
                </label>
                <textarea
                  value={product.seo_description || ''}
                  onChange={(e) => setProduct({ ...product, seo_description: e.target.value })}
                  placeholder={product.short_description || 'Brief description for search engines'}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {(product.seo_description || product.short_description || '').length}/160 characters
                </p>
              </div>

              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Search Engine Preview</p>
                <div className="space-y-1">
                  <p className="text-blue-600 dark:text-blue-400 text-lg hover:underline cursor-pointer">
                    {product.seo_title || product.name || 'Product Title'}
                  </p>
                  <p className="text-green-700 dark:text-green-500 text-sm">
                    {window.location.origin}/s/{store?.slug}/p/{product.slug || 'product-slug'}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
                    {product.seo_description || product.short_description || 'Product description will appear here...'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
                Delete Product?
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-center mb-6">
                Are you sure you want to delete "{product.name}"? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
